// What happened to this job, in Dutch.
//
// ── WHY THIS IS NOT THE AUDIT LOG ───────────────────────────────────────────────
// The audit trail exists and is complete and is unreadable on purpose: it records actor uuids, route
// keys, correlation ids and payload hashes, because its job is to answer a question a year later
// under scrutiny. A planner asking "why is Daniel on Wednesday" needs a sentence.
//
// The phrasing lives in lib/history_text.ts so it can be tested without React; this file is only the
// rendering of it.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as A from '../api/planning.ts';
import { phrase, hhmm } from '../lib/history_text.ts';
import { Spinner } from './bits.tsx';

export function History({ projectId, workpackageId, filters }: {
  projectId?: number; workpackageId?: number;
  /** Show the filter row. Off inside the task panel, where the scope is already one task. */
  filters?: { people: A.Person[]; projects: A.PlannerProject[] };
}) {
  const [f, setF] = useState<A.HistoryFilter>({});
  const active: A.HistoryFilter = {
    ...f,
    ...(projectId !== undefined && f.projectId === undefined ? { projectId } : {}),
    ...(workpackageId !== undefined ? { workpackageId } : {}),
  };
  const q = useQuery({
    queryKey: ['history', active],
    queryFn: () => A.getHistory(active),
    staleTime: 10_000,
  });

  // NOT AN ERROR BANNER ON 403. Most people cannot read the decision log by design, and telling a
  // team lead that something failed would be both untrue and an invitation to ask why.
  if (q.isError) return null;
  const list = q.data ?? [];
  const set = (k: keyof A.HistoryFilter, v: string) =>
    setF((prev) => ({ ...prev, [k]: v === '' ? undefined : (k === 'eventType' || k === 'from' || k === 'to' ? v : Number(v)) }));

  return (
    <>
      {filters && (
        // NARROWING ONLY. The route is behind `planner_events.read` already; these decide what a
        // planner is looking at, never what they are allowed to see.
        <div className="hist-filters">
          <select value={f.projectId ?? ''} onChange={(e) => set('projectId', e.target.value)} aria-label="Werf">
            <option value="">Alle werven</option>
            {filters.projects.map((p) => (
              <option key={p.projectId} value={p.projectId}>{A.werfLabel(p).name}</option>
            ))}
          </select>
          <select value={f.resourceId ?? ''} onChange={(e) => set('resourceId', e.target.value)} aria-label="Medewerker">
            <option value="">Alle medewerkers</option>
            {filters.people.filter((p) => p.resourceId !== null).map((p) => (
              <option key={p.actorId} value={p.resourceId!}>{p.displayName}</option>
            ))}
          </select>
          <select value={f.eventType ?? ''} onChange={(e) => set('eventType', e.target.value)} aria-label="Soort wijziging">
            <option value="">Alle wijzigingen</option>
            {A.EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input type="date" value={f.from ?? ''} onChange={(e) => set('from', e.target.value)} aria-label="Van" />
          <input type="date" value={f.to ?? ''} onChange={(e) => set('to', e.target.value)} aria-label="Tot en met" />
          {Object.keys(f).length > 0 && (
            <button className="btn small" onClick={() => setF({})}>Filters wissen</button>
          )}
        </div>
      )}
      {q.isPending ? <Spinner label="historie laden…" />
        : list.length === 0 ? <p className="faint">Geen wijzigingen gevonden.</p>
        : (
        <ul className="history">
          {list.map((e) => (
            <li key={e.id}>
              <time>{hhmm(e.occurredAt)}</time>
              <span>
                {phrase(e)}
                {/* The werf and the task, but only when the list spans more than one — inside a task
                    panel repeating them on every line is noise. */}
                {filters && (e.workpackageTitle || e.projectNumber) && (
                  <span className="faint"> · {[e.workpackageTitle, e.projectNumber].filter(Boolean).join(' · ')}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
